/* __next_internal_client_entry_do_not_use__ createToast,updateToast,dismissToast,isToastActive,Toast auto */ import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx } from "react/jsx-runtime";
import { Core } from "@faire/design-tokens";
import { Typography } from "@faire/web--source/slate/Typography";
import cx from "classnames";
import React from "react";
import { toast as libToast } from "react-toastify";
import { Exclamation } from "../icons/foundation/Exclamation";
import { ToastClose } from "./ToastClose";
import { ToastIcon } from "./ToastIcon";
var TIMEOUT_DURATION = {
    short: 2000,
    medium: 4000,
    long: 6000
};
function getTimeoutDuration(timeout) {
    if (typeof timeout === "string") {
        return TIMEOUT_DURATION[timeout];
    }
    if (!timeout || timeout <= 0) {
        return false;
    }
    return timeout;
}
function getLibOptions(options) {
    var _ref = options !== null && options !== void 0 ? options : {}, onClose = _ref.onClose, timeout = _ref.timeout, onOpen = _ref.onOpen, rest = _object_without_properties(_ref, [
        "onClose",
        "timeout",
        "onOpen"
    ]);
    var autoClose = getTimeoutDuration(timeout);
    var dismissible = !autoClose;
    var isDestructive = (options === null || options === void 0 ? void 0 : options.variant) === "destructive";
    var closeButton = /*#__PURE__*/ _jsx(ToastClose, {
        closeToast: getLibOnClose(onClose),
        variant: isDestructive ? "tertiary" : "primary"
    });
    var icon = isDestructive ? /*#__PURE__*/ _jsx(Exclamation, {
        height: "24px",
        width: "24px"
    }) : options === null || options === void 0 ? void 0 : options.icon;
    return _object_spread_props(_object_spread({}, rest), {
        autoClose: autoClose,
        icon: icon ? /*#__PURE__*/ _jsx(ToastIcon, {
            icon: icon
        }) : false,
        closeOnClick: false,
        pauseOnHover: false,
        isLoading: false,
        closeButton: dismissible ? closeButton : false,
        draggable: dismissible,
        className: cx({
            "Toastify__toast--dismissible": dismissible
        }),
        onOpen: getLibOnOpen(onOpen),
        onClose: getLibOnClose(onClose),
        type: isDestructive ? "error" : "default",
        theme: isDestructive ? "colored" : "light"
    });
}
function getLibContent(content) {
    return typeof content === "string" ? /*#__PURE__*/ _jsx(Typography, {
        color: Core.text.inverse,
        children: content
    }) : content;
}
function getLibOnOpen(onOpen) {
    return onOpen ? function(props) {
        return onOpen(props.toastProps.toastId, {
            data: props.data
        });
    } : undefined;
}
function getLibOnClose(onClose) {
    return onClose ? function(props) {
        return onClose(props.toastProps.toastId, {
            data: props.data
        });
    } : undefined;
}
/**
 * Creates a new toast in ToastContainer.
 * @trackfunction
 *
 * @example
 * ```
 * import { Toast, createToast } from "@faire/web--source/slate/Toast/Toast";
 *
 * createToast("This is a toast with string content.", { icon: "foundation:Truck" });
 * // => "9cf8vqy"
 *
 * Toast.create("This is a toast with string content.", { icon: "foundation:Truck" });
 * // => "tbfxltu"
 * ```
 *
 * @note Under the hood we're using `toast()` from https://fkhadra.github.io/react-toastify
 *
 * @param content What to render inside the toast body.
 * @param options The toast configuration options.
 * @returns The toast's id.
 */ export function createToast(content, options) {
    return libToast(getLibContent(content), getLibOptions(options));
}
/**
 * Updates an active toast by id.
 *
 * @example
 * ```
 * import { Toast, updateToast } from "@faire/web--source/slate/Toast/Toast";
 *
 * // update the text in the toast with id "9cf8vqy"
 * updateToast("9cf8vqy", { render: "This is the new toast string content." });
 *
 * // update the icon in the toast with id "tbfxltu"
 * Toast.update("tbfxltu", { icon: "foundation:MagicWand" });
 * ```
 *
 * @note Under the hood we're using `toast.update()` from https://fkhadra.github.io/react-toastify.
 *
 * @param toastdId The id of the toast being updated.
 * @param options The toast update options.
 */ export function updateToast(toastId, options) {
    var _ref = options !== null && options !== void 0 ? options : {}, render = _ref.render, rest = _object_without_properties(_ref, [
        "render"
    ]);
    libToast.update(toastId, _object_spread_props(_object_spread({}, getLibOptions(rest)), {
        render: render !== undefined ? getLibContent(render) : undefined
    }));
}
/**
 * Dismisses an active toast by id if specified, otherwise dismisses all active toasts.
 *
 * @example
 * ```
 * import { Toast, dismissToast } from "@faire/web--source/slate/Toast/Toast";
 *
 * // dismiss toast with id "9cf8vqy"
 * dismissToast("9cf8vqy");
 *
 * // dismiss all toasts
 * dismissToast();
 *
 * // dismiss toast with id "tbfxltu"
 * Toast.dismiss("tbfxltu");
 *
 * // dismiss all toasts
 * Toast.dismiss();
 * ```
 *
 * @note Under the hood we're using `toast.dismiss()` from https://fkhadra.github.io/react-toastify.
 *
 * @param toastdId The id of the toast being dismissed (optional).
 */ export function dismissToast(toastdId) {
    libToast.dismiss(toastdId);
}
/**
 * Checks to see if a toast is active by id.
 *
 * @example
 * ```
 * import { Toast, isToastActive } from "@faire/web--source/slate/Toast/Toast";
 *
 * isToastActive("9cf8vqy");
 * Toast.isActive("tbfxltu");
 * ```
 *
 * @note Under the hood we're using `toast.isActive()` from https://fkhadra.github.io/react-toastify.
 *
 * @param toastdId The id of the toast being dismissed.
 * @returns Whether or not it is active.
 */ export function isToastActive(toastdId) {
    return libToast.isActive(toastdId);
}
export var Toast;
(function(Toast) {
    Toast.create = createToast;
    Toast.update = updateToast;
    Toast.dismiss = dismissToast;
    Toast.isActive = isToastActive;
})(Toast || (Toast = {}));
